import AddRounded from "@mui/icons-material/AddRounded";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useRef, useState } from "react";
import { kickBlue, kickNavy } from "~/shared/styles/baseStyles";
import KickClient from "../utils/api";
import { mergeMedicationEntries } from "../utils/medicationDeduplication";
import { medicationInputStyles } from "./medicationReviewStyles";

const manualOption = {
  name: "I don’t see my medication listed",
  manual: true,
};

// The patient intake searches through the patient API. Other callers (the EMR
// editor) pass their own search, resolving to [{ name }].
const searchPatientMedications = (query, signal) =>
  KickClient.newAuthenticatedClient()
    .post("/visits/medications/search", { query }, { signal })
    .then(({ data }) => data.results || []);

export default function MedicationSearch({
  entries,
  disabled,
  onSelect,
  onManual,
  search = searchPatientMedications,
}) {
  // Read through a ref so a caller passing a new function each render does
  // not restart the search.
  const searchRef = useRef(search);
  searchRef.current = search;
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const term = query.trim();
  const currentResponse = response?.query === term ? response : null;
  const results = currentResponse?.results || [];
  const searchComplete = term.length >= 2 && !loading && !!currentResponse;

  useEffect(() => {
    if (term.length < 2 || disabled) {
      setLoading(false);
      return;
    }
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => {
      searchRef
        .current(term, controller.signal)
        .then((results) => {
          if (active)
            setResponse({
              query: term,
              results: results || [],
              error: false,
            });
        })
        .catch(() => {
          if (active) setResponse({ query: term, results: [], error: true });
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [term, disabled]);

  const alreadyAdded = (name) =>
    mergeMedicationEntries(entries, [{ name, taking: true }]).addedCount === 0;
  const message =
    term.length < 2
      ? ""
      : loading || !currentResponse
        ? "Searching medications…"
        : currentResponse.error
          ? "Search is unavailable. You can still add your medication manually."
          : !results.length
            ? "No matches found. Add your medication or supplement manually."
            : "";

  return (
    <Box>
      <Autocomplete
        id="medication-search"
        componentsProps={{
          popper: { className: "ph-no-capture" },
          paper: { className: "ph-no-capture" },
        }}
        ListboxProps={{ className: "ph-no-capture", sx: { py: 0.5 } }}
        disabled={disabled}
        open={open && term.length >= 2 && !disabled}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={null}
        inputValue={query}
        options={searchComplete ? [...results, manualOption] : []}
        filterOptions={(options) => options}
        getOptionLabel={(option) => option.name}
        getOptionDisabled={(option) =>
          !option.manual && alreadyAdded(option.name)
        }
        loading={loading}
        loadingText="Searching medications…"
        noOptionsText={message}
        onInputChange={(_, value, reason) => {
          if (reason === "input" || reason === "clear") setQuery(value);
        }}
        onChange={(_, option) => {
          if (!option) return;
          if (option.manual) {
            onManual(term);
            return;
          }
          if (alreadyAdded(option.name)) return;
          onSelect(option.name);
          setQuery("");
          setResponse(null);
        }}
        renderOption={(props, option) =>
          option.manual ? (
            <li
              {...props}
              aria-label={manualOption.name}
              style={{
                minHeight: 44,
                borderTop: results.length ? "1px solid #e9e6ef" : undefined,
                color: kickBlue,
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: 4,
                padding: "4px 12px",
              }}
            >
              {message && (
                <Typography
                  aria-hidden="true"
                  sx={{ fontSize: "13px", color: "#626176" }}
                >
                  {message}
                </Typography>
              )}
              <Box
                component="span"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <AddRounded aria-hidden="true" fontSize="small" />
                {manualOption.name}
              </Box>
            </li>
          ) : (
            <li {...props} style={{ minHeight: 48, color: kickNavy }}>
              <span>{option.name}</span>
              {alreadyAdded(option.name) && (
                <span style={{ marginLeft: "auto", paddingLeft: 12 }}>
                  Added
                </span>
              )}
            </li>
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search medications and supplements"
            placeholder="Start typing a medication name"
            sx={medicationInputStyles}
            inputProps={{
              ...params.inputProps,
              maxLength: 100,
              "aria-describedby": message
                ? "medication-search-feedback"
                : undefined,
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && (
                    <CircularProgress size={18} sx={{ color: kickBlue }} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <Typography
        id="medication-search-feedback"
        role="status"
        aria-live="polite"
        sx={{ color: "#626176", fontSize: "14px", mt: message ? 1 : 0 }}
      >
        {message}
      </Typography>
    </Box>
  );
}
